// ---- Cooldowns (reusable core) ----
// A tiny named-cooldown registry. Any action arms a cooldown by key; any system
// — gating logic OR a HUD indicator — queries how much is left / what fraction
// remains. Deliberately generic: the attack slot is the first user, but dodges,
// special abilities, consumables, spell slots, etc. all reuse the SAME registry
// and get the visible recharge sweep for free (see renderCooldownSweep + the
// `.cooldown` overlay CSS in index.html).
//
// Not networked — cooldowns are a client-side feel/gating concern (like stamina
// regen), owned by whichever system arms them. Advance once per frame with
// tickCooldowns(dt); a cooldown auto-removes itself when it reaches 0.

type CD = { total: number; remaining: number };
const cds = new Map<string, CD>();

/** Arm (or re-arm) a cooldown under `key`: `seconds` until the action is ready. */
export function startCooldown(key: string, seconds: number): void {
  if (seconds <= 0) { cds.delete(key); return; }
  cds.set(key, { total: seconds, remaining: seconds });
}

/** Seconds left before the action is ready again (0 = ready). */
export function cooldownRemaining(key: string): number {
  return cds.get(key)?.remaining ?? 0;
}

/** True while the action is still recharging. */
export function onCooldown(key: string): boolean {
  return cooldownRemaining(key) > 0;
}

/** Recharge fraction, 1 → 0 (1 = just armed, 0 = ready). Drives the HUD sweep;
 *  returns 0 when ready or unknown. */
export function cooldownFraction(key: string): number {
  const c = cds.get(key);
  if (!c || c.remaining <= 0 || c.total <= 0) return 0;
  return c.remaining / c.total;
}

/** Advance every live cooldown; call once per frame. Finished ones self-remove. */
export function tickCooldowns(dt: number): void {
  for (const [k, c] of cds) {
    c.remaining -= dt;
    if (c.remaining <= 0) cds.delete(k);
  }
}

/** Drive any HUD element's cooldown sweep from a key: writes the recharge fraction
 *  into its `--cd` CSS var (0→1), which the `.cooldown` conic-gradient overlay reads.
 *  Reusable for every action slot — one call per frame per indicator. */
export function renderCooldownSweep(el: HTMLElement, key: string): void {
  el.style.setProperty("--cd", String(cooldownFraction(key)));
}

/** Per-frame render of a cooldown-swept action-bar slot: rebuild the label only
 *  when `labelKey` changes (re-attaching the sweep overlay that innerHTML wipes),
 *  then gate + sweep. The one copy of the slot pattern — the game's attack slot
 *  (combat.ts) and the studio sandbox's (skillsim) both call it. The last-shown
 *  key rides the button's dataset, so callers keep no mirror state.
 *
 *  `hotkey` (optional): a corner keycap glyph ("1", "T", "F", …) advertising the
 *  keyboard binding. Rebuilt with the label so it survives innerHTML churn. */
export function renderSlotButton(
  btn: HTMLButtonElement,
  overlay: HTMLElement,
  labelKey: string,
  html: () => string,
  disabled: boolean,
  cdKey?: string,
  hotkey?: string,
): void {
  // Fold the hotkey into the cache key so swapping/clearing it rebuilds the slot.
  const shown = hotkey ? `${labelKey}|hk:${hotkey}` : labelKey;
  if (btn.dataset.slotShown !== shown) {
    btn.dataset.slotShown = shown;
    btn.innerHTML = html();
    if (hotkey) btn.appendChild(makeHotkeyEl(hotkey));
    btn.appendChild(overlay); // innerHTML wiped it — re-attach
  }
  btn.disabled = disabled;
  // Slots without a cooldown (e.g. the net Set/Collect slot) omit the key —
  // the overlay just stays at rest instead of borrowing an unrelated one.
  if (cdKey !== undefined) renderCooldownSweep(overlay, cdKey);
  else overlay.style.setProperty("--cd", "0");
}

/** Corner keycap glyph for action-bar slots (attack "1", Talk "T", Interact "F", …).
 *  Pure DOM — style lives on `#actionbar .slot .hotkey` in index.html. */
export function makeHotkeyEl(key: string): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = "hotkey";
  el.textContent = key;
  el.setAttribute("aria-hidden", "true");
  return el;
}

/** Attach or update a hotkey glyph on a slot that doesn't go through
 *  `renderSlotButton` (e.g. Talk's bespoke label path). Pass null to clear. */
export function setSlotHotkey(btn: HTMLElement, hotkey: string | null): void {
  let el = btn.querySelector(":scope > .hotkey") as HTMLElement | null;
  if (!hotkey) {
    el?.remove();
    return;
  }
  if (!el) {
    el = makeHotkeyEl(hotkey);
    btn.appendChild(el);
  } else if (el.textContent !== hotkey) {
    el.textContent = hotkey;
  }
}
